import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  ListItem,
  Text,
  UnorderedList,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import React from "react";
import useApi from "../../../shared/hooks/useApi";
import { PlanOfCareTypeId } from "../../../shared/schema/schema";
import { formatErrorResponse } from "../../../shared/utils/format-response-error";
import { PlanOfCareTypeItem } from "../plan-of-care-type.utils";

interface Props {
  item: PlanOfCareTypeItem;
  planOfCareTypeId: PlanOfCareTypeId;
  onClose: () => void;
  onDeactivated: () => void;
}

export default function DeactivateItemDialog(props: Props) {
  const { mutations } = useApi();
  const toast = useToast();
  const cancelRef = React.useRef<HTMLButtonElement>(null);

  const mutation = useMutation({
    ...mutations.planOfCareType.setItemActive({
      planOfCareTypeId: props.planOfCareTypeId,
      planOfCareItemId: props.item.id,
    }),
    onError: (error) =>
      toast({
        title: "Could not deactivate task",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
      }),
    onSuccess: () => {
      props.onDeactivated();
      props.onClose();
      toast({ title: "Task deactivated", status: "success", position: "top-right" });
    },
  });

  return (
    <AlertDialog isOpen={true} leastDestructiveRef={cancelRef} onClose={props.onClose}>
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader>Deactivate &quot;{props.item.label}&quot;?</AlertDialogHeader>
          <AlertDialogBody>
            <VStack align="stretch" spacing={3}>
              <Text>
                It will no longer be offered when creating a plan of care. Plans of care that
                already include it are not changed, and their PDFs stay as signed.
              </Text>
              {(props.item.dutyCount > 0 || props.item.hasEvvMapping) && (
                <UnorderedList>
                  {props.item.dutyCount > 0 && (
                    <ListItem>
                      Used by {props.item.dutyCount} existing patient plan of care{" "}
                      {props.item.dutyCount === 1 ? "duty" : "duties"}
                    </ListItem>
                  )}
                  {props.item.hasEvvMapping && (
                    <ListItem>Mapped to an EVV task code (HHAeXchange or Sandata)</ListItem>
                  )}
                </UnorderedList>
              )}
              <Text color="gray.500" fontSize="sm">
                You can reactivate it later from this screen.
              </Text>
            </VStack>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={props.onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              isLoading={mutation.isPending}
              ml={3}
              onClick={() => mutation.mutate({ isActive: false })}
            >
              Deactivate
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}
